/**
 * Tournament Detail — View tournament info, participants, bracket.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, RefreshControl, Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { fetchTournament, registerForTournament, type Tournament } from '@/lib/tournaments';
import { supabase } from '@/lib/supabase';
import BracketView from '@/components/BracketView';
import { subscribeToTournamentMatches } from '@/lib/realtimeSubscriptions';
import { ArrowLeft, Trophy, Users, Calendar, Zap, CheckCircle } from 'lucide-react-native';

interface Participant {
  profile_id: string;
  seed_number: number | null;
  status: string;
  elo_at_entry: number;
  profiles: { handle: string; display_name: string | null } | null;
}

export default function TournamentDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [hasMatches, setHasMatches] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [registering, setRegistering] = useState(false);

  const isRegistered = participants.some(p => p.profile_id === profile?.id);
  const isOpen = tournament?.status === 'registration_open' || tournament?.status === 'registration';

  const load = useCallback(async () => {
    if (!id) return;
    const [t, { data: parts }, { count }] = await Promise.all([
      fetchTournament(id),
      supabase
        .from('tournament_participants')
        .select('profile_id, seed_number, status, elo_at_entry, profiles:profile_id(handle, display_name)')
        .eq('tournament_id', id)
        .order('seed_number', { ascending: true }),
      supabase
        .from('tournament_matches')
        .select('id', { count: 'exact', head: true })
        .eq('tournament_id', id),
    ]);
    setTournament(t);
    setParticipants((parts as any) ?? []);
    setHasMatches((count ?? 0) > 0);
    setLoading(false);
    setRefreshing(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Realtime: re-fetch on tournament_matches changes
  useEffect(() => {
    if (!id) return;
    const channel = subscribeToTournamentMatches(id, () => { load(); });
    return () => { supabase.removeChannel(channel); };
  }, [id, load]);

  const handleRegister = async () => {
    if (!profile?.id || !id) return;
    setRegistering(true);
    const ok = await registerForTournament(id, profile.id);
    setRegistering(false);
    if (ok) load();
    else Alert.alert('Oops', "Couldn't register you. Please try again.");
  };

  const profileNames = new Map(
    participants.map(p => [p.profile_id, p.profiles?.display_name || p.profiles?.handle || p.profile_id.slice(0, 8)])
  );

  const formatLabels: Record<string, string> = {
    single_elimination: 'Single Elimination',
    double_elimination: 'Double Elimination',
    round_robin: 'Round Robin',
    swiss: 'Swiss',
  };

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <ActivityIndicator color={c.accent} style={{ padding: 40 }} />
      </SafeAreaView>
    );
  }

  if (!tournament) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
            <ArrowLeft size={22} color={c.text} />
          </TouchableOpacity>
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>NOT FOUND</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]} numberOfLines={1}>
          {tournament.name}
        </Text>
      </View>

      <ScrollView
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} tintColor={c.accent} />}
      >
        {/* Info Card */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          {tournament.description && (
            <Text style={[styles.desc, { color: c.textSecondary }]}>{tournament.description}</Text>
          )}

          <View style={styles.metaGrid}>
            <MetaRow icon={<Trophy size={14} color={c.accent} />} label="Format" value={formatLabels[tournament.format] || tournament.format} colors={c} />
            <MetaRow icon={<Users size={14} color={c.accent} />} label="Bracket" value={`${tournament.bracket_size} players`} colors={c} />
            <MetaRow icon={<Calendar size={14} color={c.accent} />} label="Starts" value={new Date(tournament.starts_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} colors={c} />
            {tournament.ends_at && (
              <MetaRow icon={<Calendar size={14} color={c.textTertiary} />} label="Ends" value={new Date(tournament.ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} colors={c} />
            )}
            <MetaRow icon={<Zap size={14} color={c.accent} />} label="Status" value={tournament.status.replace(/_/g, ' ').toUpperCase()} colors={c} />
            {tournament.entry_fee_cents > 0 && (
              <MetaRow label="Entry Fee" value={`$${(tournament.entry_fee_cents / 100).toFixed(0)}`} colors={c} />
            )}
            {tournament.prize_pool_cents > 0 && (
              <MetaRow label="Prize Pool" value={`$${(tournament.prize_pool_cents / 100).toFixed(0)}`} colors={c} />
            )}
            {tournament.lift_category && (
              <MetaRow label="Lift" value={tournament.lift_category} colors={c} />
            )}
          </View>
        </View>

        {/* Registration */}
        {isOpen && !isRegistered && (
          <TouchableOpacity
            style={[styles.registerBtn, { backgroundColor: c.accent }]}
            onPress={handleRegister}
            disabled={registering}
          >
            {registering ? (
              <ActivityIndicator color="#000" />
            ) : (
              <Text style={[styles.registerText, { color: '#000', fontFamily: TYPO.heading.bold }]}>REGISTER</Text>
            )}
          </TouchableOpacity>
        )}
        {isRegistered && (
          <View style={[styles.registeredBadge, { backgroundColor: (c.success || '#22c55e') + '20' }]}>
            <CheckCircle size={16} color={c.success || '#22c55e'} />
            <Text style={[styles.registeredText, { color: c.success || '#22c55e', fontFamily: TYPO.heading.bold }]}>
              Registered ✓
            </Text>
          </View>
        )}

        {/* Participants */}
        <View style={styles.section}>
          <Text style={[styles.sectionLabel, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
            PARTICIPANTS ({participants.length})
          </Text>
          {participants.length === 0 ? (
            <Text style={[{ color: c.textTertiary, fontSize: 13 }]}>No participants yet.</Text>
          ) : (
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, gap: 0 }]}>
              {participants.map((p, i) => (
                <View key={p.profile_id} style={[styles.participantRow, i > 0 && { borderTopWidth: 1, borderTopColor: c.border }]}>
                  <Text style={[styles.seedNum, { color: c.textTertiary }]}>
                    {p.seed_number ? `#${p.seed_number}` : '—'}
                  </Text>
                  <Text style={[styles.participantName, { color: c.text, fontFamily: TYPO.body.semibold }]}>
                    {p.profiles?.display_name || p.profiles?.handle || p.profile_id.slice(0, 8)}
                  </Text>
                  <Text style={[styles.eloText, { color: c.textTertiary }]}>
                    {p.elo_at_entry} Elo
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Bracket */}
        {hasMatches && (
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: c.accent, fontFamily: TYPO.heading.bold }]}>BRACKET</Text>
            <BracketView tournamentId={id!} profileNames={profileNames} />
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function MetaRow({ icon, label, value, colors }: { icon?: React.ReactNode; label: string; value: string; colors: any }) {
  return (
    <View style={styles.metaRow}>
      {icon}
      <Text style={[styles.metaLabel, { color: colors.textTertiary }]}>{label}</Text>
      <Text style={[styles.metaValue, { color: colors.text, fontFamily: TYPO.body.semibold }]}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 12 },
  backBtn: { padding: 4 },
  title: { fontSize: FONT.xl, letterSpacing: 1, flex: 1 },
  content: { padding: 16, gap: SPACE.lg, paddingBottom: 40 },
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 16, gap: SPACE.sm },
  desc: { fontSize: FONT.sm, lineHeight: 20 },
  metaGrid: { gap: SPACE.xs },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  metaLabel: { fontSize: 12, width: 80 },
  metaValue: { fontSize: 13, flex: 1 },
  registerBtn: { paddingVertical: 14, alignItems: 'center', borderRadius: RADIUS.xs },
  registerText: { fontSize: 14, letterSpacing: 1 },
  registeredBadge: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: RADIUS.xs },
  registeredText: { fontSize: 13, letterSpacing: 0.5 },
  section: { gap: SPACE.sm },
  sectionLabel: { fontSize: 11, letterSpacing: 1.5 },
  participantRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, gap: 10 },
  seedNum: { fontSize: 11, width: 30 },
  participantName: { flex: 1, fontSize: 13 },
  eloText: { fontSize: 11 },
});
